import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import SplitButton from 'react-bootstrap/SplitButton';
import {
    ArrowLeft,
    ArrowRight,
    CheckCircleFill,
    ChevronDown,
    ChevronRight,
    ClockFill,
    CodeSlash,
    GearFill,
    PencilFill,
    PlayFill,
    XCircleFill,
} from 'react-bootstrap-icons';
import apiClient, { API_ENDPOINTS } from '../api/config';
import ScriptArgumentsDialog from '../components/ScriptArgumentsDialog';
import { TEST_EXECUTION_DISABLED_MESSAGE } from '../constants/featureMessages';
import { useActiveFeatureFlags } from '../hooks/useActiveFeatureFlags';
import './AutomatedTests.css';

/* ============================================================
   Types
   ============================================================ */

interface GenerationArtifact {
    id: number;
    artifact_type: string;
}

interface GenerationJob {
    id: number;
    status: string;
    version_number?: number | null;
    created_at?: string | null;
    artifacts: GenerationArtifact[];
}

interface ExecutionItem {
    id: number;
    status: string;
    run_name: string | null;
    base_url: string | null;
    created_at: string;
    completed_at: string | null;
    total_tests: number | null;
    passed: number | null;
    failed: number | null;
    pass_rate: number | null;
}

interface ActionStep {
    type?: string;                 // e.g. NavigateAction, ClickAction, FillAction, AssertVisibleAction
    url?: string;
    locator_strategy?: string;     // id | text | css | xpath | role
    locator_value?: string;
    value?: string;
    wait_until?: string;
    raw_action?: string;
    raw_expected?: string;
    text?: string;
    [key: string]: unknown;
}

interface ResolvedActionsData {
    artifact_id: number;
    actions: Record<string, Record<string, ActionStep>>;
    edited_test_keys: string[];
    replay_ready?: boolean;
    replay_validation_errors?: string[];
    updated_at: string | null;
    titles?: Record<string, string>;
}

type TestCasesRunLocationState = {
    source?: string;
    scope?: string;
    generationId?: number | null;
    artifactId?: number | null;
    testKeys?: unknown;
    folderPath?: unknown;
} | null;

type ScopedRunRequest = {
    artifactId: number | null;
    testKeys?: string[];
    folderPath?: string[];
};

/* ============================================================
   Helpers
   ============================================================ */

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function statusBadge(status: string): React.ReactElement {
    switch (status) {
        case 'completed':
            return (
                <Badge bg="" className="at-badge at-badge--completed">
                    <CheckCircleFill size={10} className="me-1" />Completed
                </Badge>
            );
        case 'running':
            return (
                <Badge bg="" className="at-badge at-badge--running">
                    <PlayFill size={10} className="me-1" />Running
                </Badge>
            );
        case 'pending':
            return (
                <Badge bg="" className="at-badge at-badge--pending">
                    <ClockFill size={10} className="me-1" />Pending
                </Badge>
            );
        case 'failed':
            return (
                <Badge bg="" className="at-badge at-badge--failed">
                    <XCircleFill size={10} className="me-1" />Failed
                </Badge>
            );
        default:
            return <Badge bg="" className="at-badge at-badge--unknown">{status}</Badge>;
    }
}

function scopedRunTitle(request: ScopedRunRequest | null): string {
    const testCount = request?.testKeys?.length ?? 0;
    if (testCount > 0) {
        return `Run ${testCount} Selected Test${testCount === 1 ? '' : 's'}`;
    }

    if (request?.folderPath?.length) {
        return `Run Folder: ${request.folderPath.join(' / ')}`;
    }

    return 'New Execution Run';
}

/* ============================================================
   Component
   ============================================================ */

function AutomatedTestsPage(): React.ReactElement {
    const { projectId } = useParams<{ projectId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        flags: featureFlags,
        loading: featureFlagsLoading,
        refresh: refreshFeatureFlags,
    } = useActiveFeatureFlags();
    const testExecutionActionDisabled = featureFlagsLoading || !featureFlags.test_execution;

    /* ---- Data state ---- */
    const [loadingJobs, setLoadingJobs] = useState(true);
    const [jobsError, setJobsError] = useState<string | null>(null);
    const [generationJobs, setGenerationJobs] = useState<GenerationJob[]>([]);
    const [activeJob, setActiveJob] = useState<GenerationJob | null>(null);
    const [activeArtifact, setActiveArtifact] = useState<GenerationArtifact | null>(null);

    const [loadingExecutions, setLoadingExecutions] = useState(true);
    const [executions, setExecutions] = useState<ExecutionItem[]>([]);

    /* ---- Resolved actions state ---- */
    const [, setLoadingActions] = useState(false);
    const [initialActionsLoad, setInitialActionsLoad] = useState(true);
    const [resolvedActions, setResolvedActions] = useState<ResolvedActionsData | null>(null);
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState<Record<string, ActionStep>>({});
    const [savingKey, setSavingKey] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    // Fuzzy combobox state: tracks typed query and open state per step key ("testKey|stepKey")
    const [fuzzyQuery, setFuzzyQuery] = useState<Record<string, string>>({});
    const [fuzzyOpen, setFuzzyOpen] = useState<Record<string, boolean>>({});
    const [testTitleMap, setTestTitleMap] = useState<Record<string, string>>({});
    // A response for a previously selected catalog must never replace the
    // actions currently being displayed for another catalog/version.
    const actionsRequestRef = useRef(0);
    const selectedGenerationIdRef = useRef<number | null>(null);

    /* ---- Dialog state ---- */
    const [showRunDialog, setShowRunDialog] = useState(false);
    const [scopedRunRequest, setScopedRunRequest] = useState<ScopedRunRequest | null>(null);
    const [scopedRunError, setScopedRunError] = useState<string | null>(null);
    const [consumedRunState, setConsumedRunState] = useState(false);
    const [rerunNameDialog, setRerunNameDialog] = useState(false);
    const [rerunNameValue, setRerunNameValue] = useState('');
    const [rerunAllLoading, setRerunAllLoading] = useState(false);
    const [rerunMode, setRerunMode] = useState<string>('rerun');
    // Fresh run — opens full credentials dialog instead of simple name prompt
    const [freshRunDialog, setFreshRunDialog] = useState(false);

    const ensureTestExecutionStartAllowed = useCallback(() => {
        if (featureFlagsLoading) {
            void refreshFeatureFlags();
            setScopedRunError('Checking test execution access. Please try again in a moment.');
            return false;
        }

        if (!featureFlags.test_execution) {
            setScopedRunError(TEST_EXECUTION_DISABLED_MESSAGE);
            return false;
        }

        setScopedRunError(null);
        return true;
    }, [featureFlags.test_execution, featureFlagsLoading, refreshFeatureFlags]);

    /* ---- Load generation jobs ---- */
    const loadJobs = useCallback(async () => {
        if (!projectId) return;
        try {
            setLoadingJobs(true);
            setJobsError(null);
            const res = await apiClient.get<GenerationJob[]>(
                API_ENDPOINTS.PROJECT_REQUIREMENT_GENERATIONS(projectId)
            );
            const jobs = (res.data || []).filter((job) =>
                job.status === 'completed' && job.artifacts?.some((artifact) => artifact.artifact_type === 'test_cases')
            );
            setGenerationJobs(jobs);

            const requestedGenerationId = (location.state as TestCasesRunLocationState)?.generationId;
            const requestedArtifactId = (location.state as TestCasesRunLocationState)?.artifactId;
            if (typeof requestedGenerationId === 'number' && Number.isFinite(requestedGenerationId)) {
                selectedGenerationIdRef.current = requestedGenerationId;
            }

            // Keep an explicitly selected generation (including Version 1)
            // selected across refreshes.  Only default to the newest catalog
            // when there is no current selection.
            // Playwright reruns (for example job 89) carry the owning
            // test-case artifact in their manifest but are not test-case
            // generations themselves. Resolve by artifact first so a rerun
            // can never be mistaken for a new Version 2 catalog.
            const artifactOwner = typeof requestedArtifactId === 'number'
                ? jobs.find((candidate) => candidate.artifacts?.some((item) => item.id === requestedArtifactId))
                : undefined;
            const selected = artifactOwner || (selectedGenerationIdRef.current != null
                ? jobs.find((job) => job.id === selectedGenerationIdRef.current)
                : undefined);
            const job = selected || jobs[0];
            const artifact = job?.artifacts.find((item) => (
                item.artifact_type === 'test_cases' &&
                (typeof requestedArtifactId !== 'number' || item.id === requestedArtifactId)
            )) || job?.artifacts.find((item) => item.artifact_type === 'test_cases') || null;
            if (job && artifact) {
                selectedGenerationIdRef.current = job.id;
                setActiveJob(job);
                setActiveArtifact(artifact);
            } else {
                setActiveJob(null);
                setActiveArtifact(null);
            }
        } catch (err: any) {
            const raw = err?.response?.data?.detail;
            const msg = Array.isArray(raw)
                ? raw.map((e: any) => e?.msg || JSON.stringify(e)).join('; ')
                : (typeof raw === 'string' ? raw : null) || err?.message || 'Failed to load generation data';
            setJobsError(msg);
        } finally {
            setLoadingJobs(false);
        }
    }, [projectId, location.state]);

    /* ---- Load recent executions ---- */
    const loadExecutions = useCallback(async (silent = false) => {
        if (!projectId) return;
        if (!silent) setLoadingExecutions(true);
        try {
            const res = await apiClient.get<ExecutionItem[]>(
                API_ENDPOINTS.PLAYWRIGHT_EXECUTIONS(projectId)
            );
            setExecutions((res.data || []).slice(0, 5));
        } catch {
            if (!silent) setExecutions([]);
        } finally {
            if (!silent) setLoadingExecutions(false);
        }
    }, [projectId]);

    /* ---- Load resolved actions ---- */
    const loadResolvedActions = useCallback(async (artId: number, silent = false) => {
        if (!projectId) return;
        const requestId = ++actionsRequestRef.current;
        if (!silent) setLoadingActions(true);
        try {
            const res = await apiClient.get<ResolvedActionsData>(
                API_ENDPOINTS.PLAYWRIGHT_RESOLVED_ACTIONS(projectId, artId)
            );
            if (requestId !== actionsRequestRef.current) return;
            setResolvedActions(res.data);
            if (res.data?.titles) setTestTitleMap(res.data.titles);
        } catch (err: any) {
            if (requestId !== actionsRequestRef.current) return;
            if (err?.response?.status !== 404) {
                console.warn('Could not load resolved actions', err);
            }
            if (!silent) setResolvedActions(null);
        } finally {
            if (!silent && requestId === actionsRequestRef.current) setLoadingActions(false);
        }
    }, [projectId]);

    /* ---- Derived: is any execution currently running? ---- */
    const hasRunningExecution = executions.some(e =>
        ['queued', 'pending', 'running', 'retrying'].includes((e.status || '').toLowerCase())
    );
    const canStrictRerun = Boolean(resolvedActions?.replay_ready);

    /* ---- Auto-poll while a run is active (silent — no spinner flicker) ---- */
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const wasRunningRef = useRef(false);
    useEffect(() => {
        if (hasRunningExecution) {
            wasRunningRef.current = true;
            if (!pollRef.current) {
                pollRef.current = setInterval(() => {
                    void loadExecutions(true);  // silent — only need status updates during a run
                }, 10000);
            }
        } else {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            // Run just finished — do one non-silent reload to pick up step cache
            if (wasRunningRef.current) {
                wasRunningRef.current = false;
                void loadExecutions(false);
                if (activeArtifact) void loadResolvedActions(activeArtifact.id, false);
            }
        }
        return () => {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        };
    }, [hasRunningExecution, activeArtifact, loadExecutions, loadResolvedActions]);

    useEffect(() => {
        void loadJobs();
        void loadExecutions();
    }, [loadJobs, loadExecutions]);

    useEffect(() => {
        if (consumedRunState) return;

        const routeState = location.state as TestCasesRunLocationState;
        if (routeState?.source !== 'test-cases') return;

        setConsumedRunState(true);
        const rawKeys = Array.isArray(routeState.testKeys) ? routeState.testKeys : [];
        const dedupedKeys = Array.from(new Set(
            rawKeys
                .filter((value): value is string => typeof value === 'string')
                .map((value) => value.trim())
                .filter(Boolean),
        ));
        const rawFolderPath = Array.isArray(routeState.folderPath) ? routeState.folderPath : [];
        const folderPath = rawFolderPath
            .filter((value): value is string => typeof value === 'string')
            .map((value) => value.trim())
            .filter(Boolean);

        if (dedupedKeys.length === 0 && folderPath.length === 0) {
            setScopedRunError('Selected test scope was empty, so no run was started. Please go back to Test Cases and select at least one test or folder.');
            navigate(location.pathname, { replace: true, state: null });
            return;
        }

        const scopedArtifactId = typeof routeState.artifactId === 'number' && Number.isFinite(routeState.artifactId)
            ? routeState.artifactId
            : null;

        setScopedRunRequest({
            artifactId: scopedArtifactId,
            testKeys: dedupedKeys.length > 0 ? dedupedKeys : undefined,
            folderPath: dedupedKeys.length === 0 && folderPath.length > 0 ? folderPath : undefined,
        });
        setShowRunDialog(true);
        navigate(location.pathname, { replace: true, state: null });
    }, [consumedRunState, location.pathname, location.state, navigate]);

    // Load resolved actions + test titles once we know the active artifact (initial load)
    useEffect(() => {
        if (!activeArtifact || !projectId) return;
        setResolvedActions(null);
        setTestTitleMap({});
        setInitialActionsLoad(true);
        void loadResolvedActions(activeArtifact.id, false).finally(() => setInitialActionsLoad(false));
    }, [activeArtifact, projectId, loadResolvedActions]);

    const selectCatalog = useCallback((generationId: number) => {
        const job = generationJobs.find((candidate) => candidate.id === generationId);
        const artifact = job?.artifacts.find((candidate) => candidate.artifact_type === 'test_cases');
        if (!job || !artifact) return;
        selectedGenerationIdRef.current = job.id;
        actionsRequestRef.current += 1;
        setActiveJob(job);
        setActiveArtifact(artifact);
        setExpandedKeys(new Set());
        setEditingKey(null);
        setEditDraft({});
    }, [generationJobs]);

    /* ---- Resolved actions helpers ---- */
    function toggleExpand(key: string) {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }

    function startEdit(key: string) {
        if (!resolvedActions) return;
        setEditDraft({ ...resolvedActions.actions[key] });
        setEditingKey(key);
        setSaveError(null);
    }

    function cancelEdit() {
        setEditingKey(null);
        setEditDraft({});
        setSaveError(null);
    }

    async function saveEdit(key: string) {
        if (!projectId || !activeArtifact) return;
        setSavingKey(key);
        setSaveError(null);
        try {
            await apiClient.patch(
                API_ENDPOINTS.PLAYWRIGHT_PATCH_TEST_ACTIONS(projectId, activeArtifact.id, key),
                { steps: editDraft }
            );
            // Update local state
            setResolvedActions(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    actions: { ...prev.actions, [key]: editDraft },
                    edited_test_keys: prev.edited_test_keys.includes(key)
                        ? prev.edited_test_keys
                        : [...prev.edited_test_keys, key],
                };
            });
            setEditingKey(null);
            setEditDraft({});
        } catch (err: any) {
            const raw = err?.response?.data?.detail;
            const msg = Array.isArray(raw)
                ? raw.map((e: any) => e?.msg || JSON.stringify(e)).join('; ')
                : (typeof raw === 'string' ? raw : null) || 'Failed to save changes';
            setSaveError(msg);
        } finally {
            setSavingKey(null);
        }
    }

    function updateStepField(stepKey: string, field: string, value: string) {
        setEditDraft(prev => ({
            ...prev,
            [stepKey]: { ...prev[stepKey], [field]: value },
        }));
    }

    /** Insert a blank NavigateAction step immediately after `afterKey`, renumbering all subsequent steps. */
    function insertStepAfter(afterKey: string) {
        setEditDraft(prev => {
            const sorted = Object.entries(prev).sort(([a], [b]) =>
                (parseInt(a.replace(/\D/g, ''), 10) || 0) - (parseInt(b.replace(/\D/g, ''), 10) || 0)
            );
            const insertIdx = sorted.findIndex(([k]) => k === afterKey);
            const blankStep = { type: 'NavigateAction', url: '', raw_action: '', raw_expected: '' };
            const updated: [string, any][] = [
                ...sorted.slice(0, insertIdx + 1),
                ['__new__', blankStep],
                ...sorted.slice(insertIdx + 1),
            ];
            const result: Record<string, any> = {};
            updated.forEach(([, step], i) => { result[`step_${i + 1}`] = step; });
            return result;
        });
    }

    /** Remove `deleteKey` and renumber remaining steps sequentially from step_1. */
    function deleteStep(deleteKey: string) {
        setEditDraft(prev => {
            if (Object.keys(prev).length <= 1) return prev;
            const sorted = Object.entries(prev)
                .sort(([a], [b]) => (parseInt(a.replace(/\D/g, ''), 10) || 0) - (parseInt(b.replace(/\D/g, ''), 10) || 0))
                .filter(([k]) => k !== deleteKey);
            const result: Record<string, any> = {};
            sorted.forEach(([, step], i) => { result[`step_${i + 1}`] = step; });
            return result;
        });
    }

    /* ---- Render ---- */
    return (
        <div className="at-page">
            {/* Header */}
            <div className="at-page-header">
                <div className="at-page-header__left">
                    <button
                        type="button"
                        className="at-back-btn"
                        onClick={() => navigate(`/projects/${projectId}`)}
                    >
                        <ArrowLeft size={14} />
                        Back to Project
                    </button>
                    <div className="at-page-title-row">
                        <div className="at-page-icon">
                            <CodeSlash size={22} />
                        </div>
                        <div>
                            <h1 className="at-page-title">Automated Tests</h1>
                            <p className="at-page-subtitle">Playwright action scripts generated from your test cases</p>
                        </div>
                    </div>
                </div>
                <div className="at-page-header__right">
                    {executions.length > 0 ? (
                        <SplitButton
                            variant="primary"
                            size="sm"
                            className="at-new-run-btn"
                            disabled={!activeJob || !activeArtifact || rerunAllLoading || hasRunningExecution || testExecutionActionDisabled}
                            title={rerunAllLoading
                                ? <><Spinner animation="border" size="sm" className="me-1" style={{ width: 11, height: 11 }} />Starting…</>
                                : <><PlayFill size={13} className="me-1" />Re-run</>}
                            onClick={() => {
                                if (!ensureTestExecutionStartAllowed()) return;
                                const chosenMode = canStrictRerun ? 'rerun' : 'smart';
                                setRerunMode(chosenMode);
                                setRerunNameValue(
                                    chosenMode === 'rerun'
                                        ? `Re-run ${new Date().toLocaleDateString()}`
                                        : `Smart Run ${new Date().toLocaleDateString()}`
                                );
                                setRerunNameDialog(true);
                            }}
                        >
                            <Dropdown.Item
                                disabled={!canStrictRerun || testExecutionActionDisabled}
                                onClick={() => {
                                    if (!ensureTestExecutionStartAllowed()) return;
                                    if (!canStrictRerun) return;
                                    setRerunMode('rerun');
                                    setRerunNameValue(`Re-run ${new Date().toLocaleDateString()}`);
                                    setRerunNameDialog(true);
                                }}
                            >
                                <div style={{ fontWeight: 500 }}>Re-run</div>
                                <small className="text-muted">
                                    {canStrictRerun
                                        ? 'Replay-only run using compiled deterministic actions'
                                        : 'Unavailable until a Smart/Fresh run produces replay actions'}
                                </small>
                            </Dropdown.Item>
                            <Dropdown.Item
                                disabled={testExecutionActionDisabled}
                                onClick={() => {
                                    if (!ensureTestExecutionStartAllowed()) return;
                                    setRerunMode('smart');
                                    setRerunNameValue(`Smart Run ${new Date().toLocaleDateString()}`);
                                    setRerunNameDialog(true);
                                }}
                            >
                                <div style={{ fontWeight: 500 }}>Smart Run</div>
                                <small className="text-muted">Pre-generated scripts for passed tests, AI for failed/edited</small>
                            </Dropdown.Item>
                            <Dropdown.Item
                                disabled={testExecutionActionDisabled}
                                onClick={() => {
                                    if (!ensureTestExecutionStartAllowed()) return;
                                    setRerunMode('fresh');
                                    setRerunNameValue(`Fresh Run ${new Date().toLocaleDateString()}`);
                                    setFreshRunDialog(true);
                                }}
                            >
                                <div style={{ fontWeight: 500 }}>Fresh Run</div>
                                <small className="text-muted">Full AI pipeline, no pre-generated scripts used</small>
                            </Dropdown.Item>
                            <Dropdown.Item
                                disabled={testExecutionActionDisabled}
                                onClick={() => {
                                    if (!ensureTestExecutionStartAllowed()) return;
                                    setRerunMode('failed_only');
                                    setRerunNameValue(`Failed Only ${new Date().toLocaleDateString()}`);
                                    setRerunNameDialog(true);
                                }}
                            >
                                <div style={{ fontWeight: 500 }}>Failed Only</div>
                                <small className="text-muted">Re-runs failed tests, skips passing</small>
                            </Dropdown.Item>
                        </SplitButton>
                    ) : (
                        <Button
                            variant="primary"
                            size="sm"
                            className="at-new-run-btn"
                            disabled={!activeJob || !activeArtifact}
                            onClick={() => setShowRunDialog(true)}
                        >
                            <PlayFill size={13} className="me-1" />
                            New Run
                        </Button>
                    )}
                </div>
            </div>

            {jobsError && <Alert variant="danger" className="at-alert">{jobsError}</Alert>}
            {scopedRunError && (
                <Alert variant="warning" className="at-alert" dismissible onClose={() => setScopedRunError(null)}>
                    {scopedRunError}
                </Alert>
            )}

            {/* Source generation card */}
            <div className="at-section">
                <h2 className="at-section-title">Script Source</h2>
                {loadingJobs ? (
                    <div className="at-spinner-row">
                        <Spinner animation="border" size="sm" />
                        <span>Loading generation data…</span>
                    </div>
                ) : activeJob ? (
                    <div className="at-source-card">
                        <div className="at-source-card__info">
                            <span className="at-source-label">Test Cases Catalog</span>
                            {generationJobs.length > 1 ? (
                                <Form.Select
                                    aria-label="Test case catalog version"
                                    size="sm"
                                    value={activeJob.id}
                                    onChange={(event) => selectCatalog(Number(event.target.value))}
                                    style={{ width: 'auto', minWidth: 170 }}
                                >
                                    {generationJobs.map((job) => (
                                        <option key={job.id} value={job.id}>
                                            {job.version_number ? `Version ${job.version_number}` : `Generation #${job.id}`}
                                        </option>
                                    ))}
                                </Form.Select>
                            ) : (
                                <span className="at-source-version">
                                    {activeJob.version_number ? `v${activeJob.version_number}` : `Generation #${activeJob.id}`}
                                </span>
                            )}
                            <span className="at-source-date">{formatDate(activeJob.created_at ?? null)}</span>
                        </div>
                        <div className="at-source-card__actions">
                            <Badge bg="" className="at-badge at-badge--completed">
                                <CheckCircleFill size={10} className="me-1" />Active
                            </Badge>
                            <Link
                                to={`/projects/${projectId}?tab=test-cases`}
                                className="at-source-link"
                            >
                                View Test Cases <ArrowRight size={12} />
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="at-empty-state">
                        <p>No completed test case generation found for this project.</p>
                        <Link to={`/projects/${projectId}?tab=test-cases`}>
                            Generate test cases first →
                        </Link>
                    </div>
                )}
            </div>

            {/* Resolved Actions (step cache) */}
            {activeArtifact && (
                <div className="at-section">
                    <div className="at-section-header">
                        <h2 className="at-section-title">Test Step Actions</h2>
                        {resolvedActions && (
                            <span className="at-section-meta">
                                {Object.keys(resolvedActions.actions).length} test cases cached
                                {resolvedActions.updated_at && (
                                    <> · last updated {formatDate(resolvedActions.updated_at)}</>
                                )}
                            </span>
                        )}
                    </div>

                    {saveError && <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>{saveError}</Alert>}

                    {initialActionsLoad ? (
                        <div className="at-spinner-row">
                            <Spinner animation="border" size="sm" />
                            <span>Loading resolved actions…</span>
                        </div>
                    ) : hasRunningExecution ? (
                        <div className="at-run-inprogress-banner">
                            <div className="at-run-inprogress-banner__icon">
                                <GearFill size={18} className="at-spin" />
                            </div>
                            <div>
                                <strong>Run in progress</strong>
                                <p>The Playwright pipeline is executing your test cases. Step actions will appear here automatically once the run completes.</p>
                            </div>
                        </div>
                    ) : !resolvedActions || Object.keys(resolvedActions.actions).length === 0 ? (
                        <div className="at-empty-state">
                            <p>No cached actions yet. Run tests at least once to generate the step cache.</p>
                        </div>
                    ) : (
                        <div className="at-actions-list">
                            {Object.entries(resolvedActions.actions).map(([testKey, steps]) => {
                                const isExpanded = expandedKeys.has(testKey);
                                const isEditing = editingKey === testKey;
                                const isModified = resolvedActions.edited_test_keys.includes(testKey);
                                const stepEntries = Object.entries(isEditing ? editDraft : steps);
                                return (
                                    <div key={testKey} className="at-action-card">
                                        <div
                                            className="at-action-card__header"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => toggleExpand(testKey)}
                                            onKeyDown={e => e.key === 'Enter' && toggleExpand(testKey)}
                                        >
                                            <span className="at-action-card__chevron">
                                                {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                            </span>
                                            <span className="at-action-card__key">
                                                {testTitleMap[testKey] || testKey}
                                                {testTitleMap[testKey] && (
                                                    <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.78rem', marginLeft: 6 }}>{testKey}</span>
                                                )}
                                            </span>
                                            <span className="at-action-card__count">
                                                {Object.keys(steps).length} step{Object.keys(steps).length !== 1 ? 's' : ''}
                                            </span>
                                            {isModified && (
                                                <Badge bg="" className="at-badge at-badge--modified ms-2">Modified</Badge>
                                            )}
                                            {!isEditing && (
                                                <button
                                                    type="button"
                                                    className="at-edit-btn ms-auto"
                                                    onClick={e => { e.stopPropagation(); startEdit(testKey); if (!isExpanded) toggleExpand(testKey); }}
                                                >
                                                    <PencilFill size={11} className="me-1" />Edit
                                                </button>
                                            )}
                                        </div>

                                        {isExpanded && (
                                            <div className="at-action-card__body">
                                                <div className="at-steps-table-wrap" role="region" aria-label={`${testTitleMap[testKey] || testKey} steps`} tabIndex={0}>
                                                <table className="at-steps-table" data-responsive-cards="true">
                                                    <thead>
                                                        <tr>
                                                            <th style={{width:'60px'}}>Step</th>
                                                            <th style={{width:'210px'}}>Action</th>
                                                            <th>Details</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {stepEntries.map(([stepKey, step]) => {
                                                            const actionType = (step.type as string) || '';

                                                            // Standard action types shown as normal options
                                                            const ACTION_TYPES_STANDARD = [
                                                                'NavigateAction',
                                                                'ClickAction',
                                                                'FillAction',
                                                                'SelectAction',
                                                                'AssertVisibleAction',
                                                                'AssertTextAction',
                                                                'AssertPageContainsAction',
                                                                'WaitAction',
                                                                'NetworkAssertAction',
                                                                'ScreenshotAction',
                                                            ] as const;
                                                            // Advanced actions shown under a separate optgroup
                                                            const ACTION_TYPES_ADVANCED = [
                                                                'FillAllRequiredAction',
                                                            ] as const;
                                                            const ALL_ACTION_TYPES = [...ACTION_TYPES_STANDARD, ...ACTION_TYPES_ADVANCED];

                                                            const LOCATOR_STRATEGIES = ['id', 'name', 'css', 'xpath', 'role', 'text', 'placeholder', 'data-testid'];

                                                            const fieldsByType: Record<string, string[]> = {
                                                                NavigateAction:          ['url', 'wait_until'],
                                                                ClickAction:             ['locator_strategy', 'locator_value'],
                                                                FillAction:              ['locator_strategy', 'locator_value', 'value'],
                                                                SelectAction:            ['locator_strategy', 'locator_value', 'option_value'],
                                                                AssertVisibleAction:     ['locator_strategy', 'locator_value'],
                                                                AssertTextAction:        ['locator_strategy', 'locator_value', 'expected_text'],
                                                                AssertPageContainsAction:['text', 'should_exist'],
                                                                WaitAction:              ['condition', 'locator_strategy', 'locator_value', 'timeout'],
                                                                NetworkAssertAction:     ['assertion_type', 'endpoint_pattern', 'expected_status', 'method_filter'],
                                                                ScreenshotAction:        ['label'],
                                                                FillAllRequiredAction:   ['submit_after'],
                                                            };

                                                            const editFields = fieldsByType[actionType] || ['url', 'locator_strategy', 'locator_value', 'value'];

                                                            // When user picks a new action type, preserve overlapping fields and blank-init new ones
                                                            const handleActionTypeChange = (newType: string) => {
                                                                const newFields = fieldsByType[newType] || [];
                                                                // Start with raw metadata
                                                                const next: Record<string, unknown> = { type: newType };
                                                                if (step.raw_action)   next['raw_action']   = step.raw_action;
                                                                if (step.raw_expected) next['raw_expected'] = step.raw_expected;
                                                                // Carry over any field value that exists in both old and new type
                                                                newFields.forEach(f => {
                                                                    next[f] = (step[f] !== undefined && step[f] !== '') ? step[f] : '';
                                                                });
                                                                setEditDraft(prev => ({ ...prev, [stepKey]: next as any }));
                                                            };

                                                            // Build a compact readonly summary line for Playwright actions
                                                            const summarize = () => {
                                                                if (step.url) return step.url as string;
                                                                if (step.locator_strategy && step.locator_value) {
                                                                    const base = `${step.locator_strategy}="${step.locator_value}"`;
                                                                    return step.value ? `${base} → "${step.value}"` : base;
                                                                }
                                                                return (step.value as string) || (step.raw_action as string) || '—';
                                                            };

                                                            // Fields that use a sub-dropdown instead of a plain text input
                                                            const isLocatorStrategyField = (f: string) => f === 'locator_strategy';
                                                            const isWaitUntilField       = (f: string) => f === 'wait_until';
                                                            const isShouldExistField     = (f: string) => f === 'should_exist';
                                                            const isSubmitAfterField     = (f: string) => f === 'submit_after';

                                                            return (
                                                                <tr key={stepKey}>
                                                                    <td className="at-step-key" data-label="Step">
                                                                        {stepKey}
                                                                    </td>
                                                                    <td data-label="Action">
                                                                        {isEditing ? (() => {
                                                                            const comboKey = `${testKey}|${stepKey}`;
                                                                            const query = fuzzyQuery[comboKey] ?? actionType;
                                                                            const isOpen = !!fuzzyOpen[comboKey];
                                                                            const filtered = ALL_ACTION_TYPES.filter(t =>
                                                                                t.toLowerCase().includes(query.toLowerCase())
                                                                            );
                                                                            return (
                                                                                <div style={{ position: 'relative', width: '205px' }}>
                                                                                    <input
                                                                                        className="at-step-input"
                                                                                        style={{ width: '205px', maxWidth: '205px', minWidth: 0 }}
                                                                                        value={query}
                                                                                        placeholder="Type to search action..."
                                                                                        autoComplete="off"
                                                                                        onChange={e => {
                                                                                            setFuzzyQuery(prev => ({ ...prev, [comboKey]: e.target.value }));
                                                                                            setFuzzyOpen(prev => ({ ...prev, [comboKey]: true }));
                                                                                        }}
                                                                                        onFocus={() => setFuzzyOpen(prev => ({ ...prev, [comboKey]: true }))}
                                                                                        onBlur={() => setTimeout(() => setFuzzyOpen(prev => ({ ...prev, [comboKey]: false })), 150)}
                                                                                    />
                                                                                    {isOpen && filtered.length > 0 && (
                                                                                        <div style={{
                                                                                            position: 'absolute', top: '100%', left: 0, zIndex: 9999,
                                                                                            background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                                                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '220px',
                                                                                            maxHeight: 220, overflowY: 'auto', marginTop: 2,
                                                                                        }}>
                                                                                            {filtered.map(t => (
                                                                                                <div
                                                                                                    key={t}
                                                                                                    onMouseDown={() => {
                                                                                                        handleActionTypeChange(t);
                                                                                                        setFuzzyQuery(prev => ({ ...prev, [comboKey]: t }));
                                                                                                        setFuzzyOpen(prev => ({ ...prev, [comboKey]: false }));
                                                                                                    }}
                                                                                                    style={{
                                                                                                        padding: '6px 12px', fontSize: '0.82rem', cursor: 'pointer',
                                                                                                        background: t === actionType ? '#eff6ff' : 'transparent',
                                                                                                        color: ACTION_TYPES_ADVANCED.includes(t as any) ? '#92400e' : '#1e293b',
                                                                                                        borderBottom: '1px solid #f1f5f9',
                                                                                                    }}
                                                                                                    onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
                                                                                                    onMouseLeave={e => (e.currentTarget.style.background = t === actionType ? '#eff6ff' : 'transparent')}
                                                                                                >
                                                                                                    {t}{ACTION_TYPES_ADVANCED.includes(t as any) ? ' (advanced)' : ''}
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })() : (
                                                                            <code className="at-step-action">{actionType || '—'}</code>
                                                                        )}
                                                                    </td>
                                                                    <td data-label="Details">
                                                                        {isEditing ? (
                                                                            <>
                                                                            <div className="at-step-params-edit">
                                                                                {editFields.map(field => (
                                                                                    <label key={field} className="at-param-label">
                                                                                        <span>{field}</span>
                                                                                        {isLocatorStrategyField(field) ? (
                                                                                            <select
                                                                                                className="at-step-input"
                                                                                                value={(step[field] as string) || ''}
                                                                                                onChange={e => updateStepField(stepKey, field, e.target.value)}
                                                                                                style={{ cursor: 'pointer' }}
                                                                                            >
                                                                                                <option value="">— select strategy —</option>
                                                                                                {LOCATOR_STRATEGIES.map(s => (
                                                                                                    <option key={s} value={s}>{s}</option>
                                                                                                ))}
                                                                                            </select>
                                                                                        ) : isWaitUntilField(field) ? (
                                                                                            <select
                                                                                                className="at-step-input"
                                                                                                value={(step[field] as string) || 'domcontentloaded'}
                                                                                                onChange={e => updateStepField(stepKey, field, e.target.value)}
                                                                                                style={{ cursor: 'pointer' }}
                                                                                            >
                                                                                                <option value="domcontentloaded">domcontentloaded</option>
                                                                                                <option value="load">load</option>
                                                                                                <option value="networkidle">networkidle</option>
                                                                                            </select>
                                                                                        ) : isShouldExistField(field) || isSubmitAfterField(field) ? (
                                                                                            <select
                                                                                                className="at-step-input"
                                                                                                value={String(step[field] ?? 'true')}
                                                                                                onChange={e => updateStepField(stepKey, field, e.target.value)}
                                                                                                style={{ cursor: 'pointer' }}
                                                                                            >
                                                                                                <option value="true">true</option>
                                                                                                <option value="false">false</option>
                                                                                            </select>
                                                                                        ) : (
                                                                                            <input
                                                                                                className="at-step-input"
                                                                                                value={(step[field] as string) || ''}
                                                                                                placeholder={`${field}…`}
                                                                                                onChange={e => updateStepField(stepKey, field, e.target.value)}
                                                                                            />
                                                                                        )}
                                                                                    </label>
                                                                                ))}
                                                                            </div>
                                                                            <div style={{ display: 'flex', gap: 3, marginTop: 6, justifyContent: 'flex-end' }}>
                                                                                <button
                                                                                    type="button"
                                                                                    title="Insert step after this one"
                                                                                    onClick={() => insertStepAfter(stepKey)}
                                                                                    style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 4, width: 20, height: 20, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                                >+</button>
                                                                                <button
                                                                                    type="button"
                                                                                    title="Delete this step"
                                                                                    onClick={() => deleteStep(stepKey)}
                                                                                    disabled={Object.keys(editDraft).length <= 1}
                                                                                    style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, width: 20, height: 20, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: Object.keys(editDraft).length <= 1 ? 0.4 : 1 }}
                                                                                >−</button>
                                                                            </div>
                                                                            </>
                                                                        ) : (
                                                                            <div className="at-step-manifest-detail">
                                                                                <span className="at-step-params">{summarize() as string}</span>
                                                                                {step.raw_action && (
                                                                                    <span className="at-step-expected" style={{opacity:0.75}}>{step.raw_action as string}</span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                                </div>

                                                {isEditing && (
                                                    <div className="at-action-edit-footer">
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            disabled={savingKey === testKey}
                                                            onClick={() => void saveEdit(testKey)}
                                                        >
                                                            {savingKey === testKey ? (
                                                                <><Spinner animation="border" size="sm" className="me-1" />Saving…</>
                                                            ) : 'Save Changes'}
                                                        </Button>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            disabled={savingKey === testKey}
                                                            onClick={cancelEdit}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* How it works */}            <div className="at-section">
                <h2 className="at-section-title">How Scripts Work</h2>
                <div className="at-how-it-works">
                    <div className="at-how-step">
                        <div className="at-how-step__num">1</div>
                        <div>
                            <strong>Plan</strong>
                            <p>Each test case is planned into a sequence of Playwright actions (navigate, fill, click, assert).</p>
                        </div>
                    </div>
                    <div className="at-how-step">
                        <div className="at-how-step__num">2</div>
                        <div>
                            <strong>Resolve</strong>
                            <p>Actions are resolved by the AI interpreter on first run and cached — subsequent runs skip AI calls entirely.</p>
                        </div>
                    </div>
                    <div className="at-how-step">
                        <div className="at-how-step__num">3</div>
                        <div>
                            <strong>Execute</strong>
                            <p>The Playwright runner executes each action directly against your target URL and records results.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent executions */}
            <div className="at-section">
                <div className="at-section-header">
                    <h2 className="at-section-title">Recent Executions</h2>
                    <Link to={`/projects/${projectId}?tab=executions`} className="at-section-link">
                        View all <ArrowRight size={12} />
                    </Link>
                </div>
                {loadingExecutions ? (
                    <div className="at-spinner-row">
                        <Spinner animation="border" size="sm" />
                        <span>Loading executions…</span>
                    </div>
                ) : executions.length === 0 ? (
                    <div className="at-empty-state">
                        <p>No executions yet. Click <strong>New Run</strong> to get started.</p>
                    </div>
                ) : (
                    <div className="at-exec-list">
                        {executions.map((exec) => (
                            <div key={exec.id} className="at-exec-row">
                                <div className="at-exec-row__info">
                                    <span className="at-exec-name">
                                        {exec.run_name || `Run #${exec.id}`}
                                    </span>
                                    <span className="at-exec-date">{formatDate(exec.created_at)}</span>
                                </div>
                                <div className="at-exec-row__stats">
                                    {exec.total_tests != null && (
                                        <span className="at-exec-stat">
                                            {exec.passed ?? 0}/{exec.total_tests} passed
                                        </span>
                                    )}
                                    {statusBadge(exec.status)}
                                    <Link
                                        to={`/projects/${projectId}?tab=executions`}
                                        className="at-exec-detail-link"
                                    >
                                        Details <ArrowRight size={11} />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Run dialog */}
            {showRunDialog && (
                <ScriptArgumentsDialog
                    show={showRunDialog}
                    title={scopedRunTitle(scopedRunRequest)}
                    projectId={projectId ? Number(projectId) : null}
                    artifactId={scopedRunRequest?.artifactId ?? activeArtifact?.id ?? null}
                    testKeys={scopedRunRequest?.testKeys}
                    folderPath={scopedRunRequest?.folderPath}
                    onHide={() => {
                        setShowRunDialog(false);
                        setScopedRunRequest(null);
                    }}
                    onRunStarted={() => {
                        setShowRunDialog(false);
                        setScopedRunRequest(null);
                        void loadExecutions();
                    }}
                />
            )}

            {/* Fresh Run dialog — full credentials popup */}
            <ScriptArgumentsDialog
                show={freshRunDialog}
                title="Fresh Run"
                projectId={projectId ? Number(projectId) : null}
                artifactId={activeArtifact?.id ?? null}
                defaultRunMode="fresh"
                onHide={() => setFreshRunDialog(false)}
                onRunStarted={() => {
                    setFreshRunDialog(false);
                    void loadExecutions();
                }}
            />

            {/* Run name dialog */}
            <Modal show={rerunNameDialog} onHide={() => setRerunNameDialog(false)} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1rem' }}>
                        {{ smart: 'Smart Run', fresh: 'Fresh Run', failed_only: 'Failed Only', rerun: 'Re-run' }[rerunMode] ?? 'Run'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {rerunMode === 'rerun' && !canStrictRerun && (
                        <Alert variant="warning" className="mb-2">
                            Re-run requires compiled replay actions. This run will use Smart mode instead.
                        </Alert>
                    )}
                    <Form.Control
                        autoFocus
                        type="text"
                        placeholder="Run name"
                        value={rerunNameValue}
                        onChange={e => setRerunNameValue(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" size="sm" onClick={() => setRerunNameDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={rerunAllLoading || !rerunNameValue.trim() || testExecutionActionDisabled}
                        onClick={async () => {
                            if (!activeArtifact || !projectId) return;
                            if (!ensureTestExecutionStartAllowed()) return;
                            const sourceExecution = executions.find(e => e.base_url);
                            const lastBaseUrl = sourceExecution?.base_url ?? '';
                            if (!lastBaseUrl) { alert('No base URL found from previous runs.'); return; }
                            setRerunAllLoading(true);
                            setRerunNameDialog(false);
                            try {
                                const effectiveMode = (rerunMode === 'rerun' && !canStrictRerun) ? 'smart' : rerunMode;
                                const payload: Record<string, any> = {
                                    project_id: Number(projectId),
                                    source_job_id: sourceExecution?.id,
                                    artifact_id: activeArtifact.id,
                                    run_mode: effectiveMode,
                                    run_name: rerunNameValue.trim(),
                                    base_url: lastBaseUrl,
                                };
                                await apiClient.post(API_ENDPOINTS.PLAYWRIGHT_CREATE_JOB(Number(projectId)), payload);
                                void loadExecutions();
                            } catch (err: any) {
                                const msg = err?.response?.data?.detail || err?.message || 'Failed to start run';
                                alert(msg);
                            } finally {
                                setRerunAllLoading(false);
                            }
                        }}
                    >
                        {rerunAllLoading
                            ? <><Spinner animation="border" size="sm" className="me-1" style={{ width: 10, height: 10 }} />Starting…</>
                            : 'Start'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default AutomatedTestsPage;
